import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { apiFetch } from '../lib/api';
import { Dialog } from './Dialog';

const pad = (value: number) => String(value).padStart(2, '0');
const dateValue = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const daysAgo = (days: number) => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
};

/**
 * UGRAS DATUMRA (versenyaudit U6).
 *
 * A dátumválasztó egy NAPOT kér; a csatorna az aznapi elso uzenetnel nyilik
 * meg (ha aznap nem irt senki, a kovetkezonel). A gorgetest a meglevo
 * permalink-ut vegzi (`?m=`), ugyanugy kiemelve, mint egy megosztott linknel.
 */
export function JumpToDateDialog({
  channelId,
  open,
  onOpenChange,
  initial,
}: {
  channelId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ahonnan nyitottak (a kattintott nap) - a mezo ezzel indul. */
  initial: Date;
}) {
  const navigate = useNavigate();
  const [value, setValue] = useState(dateValue(initial));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const jump = async (day: Date) => {
    setBusy(true);
    setError(null);
    try {
      const { messageId } = await apiFetch<{ messageId: string | null }>(
        `/api/channels/${channelId}/messages/at?date=${encodeURIComponent(day.toISOString())}`,
      );
      if (!messageId) {
        setError('Ebben a csatornában még nincs üzenet.');
        return;
      }
      onOpenChange(false);
      void navigate({ to: '/c/$channelId', params: { channelId }, search: { m: messageId } });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Nem sikerült odaugrani.');
    } finally {
      setBusy(false);
    }
  };

  const quick: { label: string; day: Date }[] = [
    { label: 'Ma', day: daysAgo(0) },
    { label: 'Tegnap', day: daysAgo(1) },
    { label: 'Egy hete', day: daysAgo(7) },
    { label: 'Egy hónapja', day: daysAgo(30) },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Ugrás dátumra">
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap gap-2">
          {quick.map((item) => (
            <button
              key={item.label}
              type="button"
              disabled={busy}
              onClick={() => void jump(item.day)}
              className="rounded-pill border border-border px-3 py-1 text-xs hover:bg-surface-hover disabled:opacity-50"
            >
              {item.label}
            </button>
          ))}
        </div>
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const [year, month, day] = value.split('-').map(Number);
            if (year && month && day) void jump(new Date(year, month - 1, day));
          }}
        >
          <input
            type="date"
            value={value}
            max={dateValue(new Date())}
            onChange={(event) => setValue(event.target.value)}
            className="min-w-0 flex-1 rounded-control border border-border bg-surface px-3 py-2"
          />
          <button
            type="submit"
            disabled={busy || !value}
            className="rounded-control bg-accent px-4 py-2 font-semibold text-accent-ink hover:bg-accent-hover disabled:opacity-50"
          >
            Ugrás
          </button>
        </form>
        {error && (
          <p role="alert" className="text-xs text-danger">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
